import { Box, Stack, Typography, useTheme } from "@mui/material";
import { PRODUCT_HUB_WORKFLOW_CARD_HEIGHT_SX, FeatureStudioHierarchyCard } from "../feature-studio/FeatureStudioHierarchyCard";
import { StrengthWheel } from "../meshStrength/StrengthWheel";
import SddWorkflow from "./SddWorkflow";

const CONTEXT_ITEMS = [
    ["Discover Context", "What the product is, the problem it solves, and who it serves."],
    ["External Context", "Jira or Azure DevOps — live backlog, sprints, and work items."],
    ["File Context", "Specs, personas, designs, and other artifacts you upload."],
    ["Code Context", "Linked repositories and the code inside them."]
] as const;

const StepHeading = ({ number, children }: { number: number; children: React.ReactNode }) => (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
        <Box
            component="span"
            sx={{
                flex: "0 0 auto",
                borderRadius: 999,
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                px: 0.875,
                py: 0.25,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.03em",
                lineHeight: 1.35
            }}
        >
            Step {number}
        </Box>
        <Typography component="h3" sx={{ minWidth: 0, fontSize: 12, color: "text.secondary", lineHeight: 1.35 }}>
            {children}
        </Typography>
    </Stack>
);

const ContextMeshOverview = () => (
    <Box sx={{ width: 96, flex: "0 0 96px", textAlign: "center" }}>
        <Typography
            sx={{
                mb: 0.5,
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: "text.secondary",
                lineHeight: 1.15,
                whiteSpace: "nowrap"
            }}
        >
            Context Mesh
        </Typography>
        <Box sx={{ width: 84, height: 84, mx: "auto" }}>
            <StrengthWheel score={0} showHub={false} showLabels={false} showScoreGauge={false} />
        </Box>
    </Box>
);

interface ProductDevelopmentProcessProps {
    variant?: "product_studio" | "context_mesh";
}

const ProductDevelopmentProcess = ({ variant = "product_studio" }: ProductDevelopmentProcessProps) => {
    const theme = useTheme();
    const contextMesh = variant === "context_mesh";
    const panelSx = {
        minWidth: 0,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1.125,
        backgroundColor: "background.paper",
        p: 1.25
    };

    if (contextMesh) {
        return (
            <Box
                component="section"
                aria-label="Context Mesh guide"
                sx={{ minWidth: 0, height: "100%", p: { xs: 2, sm: 2.75, lg: "22px 26px" }, display: "flex", flexDirection: "column", justifyContent: "center" }}
            >
                <Typography sx={{ fontSize: { xs: 18, md: 22 }, lineHeight: 1.65, fontWeight: 700, color: "text.secondary", mb: 1 }}>
                    Analyze product context. Improve that context to build better products.
                </Typography>
                <Box sx={{ ...panelSx, flex: 1, minHeight: 0, p: { xs: 2.5, lg: "20px 28px" }, display: "flex", alignItems: "center" }}>
                    <Stack direction={{ xs: "column", md: "row" }} alignItems="center" spacing={{ xs: 2.5, md: 4.5 }} sx={{ width: "100%" }}>
                        <Box sx={{ width: 180, flex: "0 0 auto" }}>
                            <Typography sx={{ textAlign: "center", fontSize: 20, lineHeight: 1.3, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.disabled", mb: 1.25 }}>
                                Context Mesh
                            </Typography>
                            <StrengthWheel score={0} showHub={false} showScoreGauge={false} uid="hub-overview" />
                        </Box>
                        <Stack spacing={2.25} sx={{ flex: 1, minWidth: 0 }}>
                            {CONTEXT_ITEMS.map(([label, description], index) => (
                                <Typography key={label} color="text.secondary" sx={{ fontSize: { xs: 14, md: 17 }, lineHeight: 1.5 }}>
                                    <Box component="strong" sx={{ color: index === 2 || index === 3 ? "#1954ED" : "#103699", fontWeight: 700 }}>
                                        {label}
                                    </Box>
                                    {" — "}
                                    {description}
                                </Typography>
                            ))}
                        </Stack>
                    </Stack>
                </Box>
            </Box>
        );
    }

    return (
        <Box component="section" aria-label="Three-step product development process" sx={{ minWidth: 0, p: { xs: 2, sm: 2.75, lg: "22px 26px" } }}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "minmax(0, 1fr)",
                        sm: "minmax(0, 1.2fr) minmax(0, 1fr)"
                    },
                    alignItems: "stretch",
                    gap: 1.25,
                    mb: 2
                }}
            >
                <Box
                    component="section"
                    aria-labelledby="product-process-step-1"
                    sx={{ width: "100%", maxWidth: { xs: "534px", sm: "none" }, minWidth: 0, display: "flex", flexDirection: "column" }}
                >
                    <StepHeading number={1}>
                        <span id="product-process-step-1">Provide product context</span>
                    </StepHeading>
                    <Box sx={{ ...panelSx, ...PRODUCT_HUB_WORKFLOW_CARD_HEIGHT_SX, flex: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
                        <ContextMeshOverview />
                        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0, maxWidth: "100%" }}>
                            {CONTEXT_ITEMS.map(([label, description]) => (
                                <Typography
                                    key={label}
                                    sx={{
                                        fontSize: 11.5,
                                        lineHeight: 1.4,
                                        color: "text.secondary",
                                        whiteSpace: "normal",
                                        overflowWrap: "anywhere"
                                    }}
                                >
                                    <Box component="strong" sx={{ color: "text.primary", fontWeight: 600 }}>
                                        {label}
                                    </Box>
                                    {" — "}
                                    {description}
                                </Typography>
                            ))}
                        </Stack>
                    </Box>
                </Box>

                <Box
                    component="section"
                    aria-labelledby="product-process-step-2"
                    sx={{ width: "100%", maxWidth: { xs: "360px", sm: "none" }, minWidth: 0, display: "flex", flexDirection: "column" }}
                >
                    <StepHeading number={2}>
                        <span id="product-process-step-2">Define the Product, Feature and Stories</span>
                    </StepHeading>
                    <FeatureStudioHierarchyCard compact />
                </Box>
            </Box>

            <Box component="section" aria-labelledby="product-process-step-3">
                <StepHeading number={3}>
                    <span id="product-process-step-3">Execute the SDD to build the product</span>
                </StepHeading>
                <SddWorkflow />
            </Box>
        </Box>
    );
};

export default ProductDevelopmentProcess;
